import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import CoverageApi, { CourtCoverageDto, CoveringAttorneyDto } from '../../api/CoverageApi';

interface AttorneySummary {
  guid: string;
  name: string;
  appearances: number;
  lastOn: string;
}

/** One line per attorney, most recent first, from the court's coverage history. */
function summarise(rows: CourtCoverageDto[]): AttorneySummary[] {
  const byAttorney = new Map<string, AttorneySummary>();
  for (const row of rows) {
    if (!row.attorneyGuid) continue;
    const current = byAttorney.get(row.attorneyGuid);
    if (current) {
      current.appearances += 1;
      if (row.CourtCoverage_CoveredOn > current.lastOn) current.lastOn = row.CourtCoverage_CoveredOn;
    } else {
      byAttorney.set(row.attorneyGuid, {
        guid: row.attorneyGuid,
        name: row.attorneyName ?? 'Unnamed attorney',
        appearances: 1,
        lastOn: row.CourtCoverage_CoveredOn,
      });
    }
  }
  return [...byAttorney.values()].sort((a, b) => b.lastOn.localeCompare(a.lastOn));
}

/**
 * "The court page will link to its prosecutors and its covering attorneys" (6 Oct 2026).
 *
 * Two lists, because they are two different facts. **Covers this court** is who the firm says will
 * cover it, added here without a case (Lloyd, 8 Oct 2026: he added Gordon Slade to San Antonio
 * Municipal "generically" and nothing showed, because this used to list appearances only).
 * **Appearances** is who actually stood up here, from the recorded history, and cannot be edited.
 */
const CourtCoveringAttorneys: React.FC<{ courtGuid: string }> = ({ courtGuid }) => {
  const [listed, setListed] = useState<CoveringAttorneyDto[] | null>(null);
  const [appeared, setAppeared] = useState<AttorneySummary[] | null>(null);
  const [allAttorneys, setAllAttorneys] = useState<CoveringAttorneyDto[]>([]);
  const [choice, setChoice] = useState<CoveringAttorneyDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [onCourt, coverage, attorneys] = await Promise.all([
      CoverageApi.listedForCourt(courtGuid),
      CoverageApi.listCoverage({ courtGuid }),
      CoverageApi.listAttorneys(),
    ]);
    setListed(onCourt);
    setAppeared(summarise(coverage));
    setAllAttorneys(attorneys);
  }, [courtGuid]);

  useEffect(() => {
    let cancelled = false;
    load().catch(() => !cancelled && setError('Could not load the covering attorneys.'));
    return () => {
      cancelled = true;
    };
  }, [load]);

  const change = async (action: () => Promise<void>) => {
    try {
      setSaving(true);
      setError(null);
      await action();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the change.');
    } finally {
      setSaving(false);
    }
  };

  const add = () =>
    choice &&
    change(async () => {
      await CoverageApi.addCourt(choice.CoveringAttorney_guid, courtGuid);
      setChoice(null);
    });

  if (!listed || !appeared) {
    return error ? (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    ) : (
      <CircularProgress size={20} />
    );
  }

  const listedGuids = new Set(listed.map((a) => a.CoveringAttorney_guid));
  const addable = allAttorneys.filter((a) => !listedGuids.has(a.CoveringAttorney_guid));

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Covers this court
      </Typography>
      {listed.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Nobody is listed yet.
        </Typography>
      ) : (
        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
          {listed.map((a) => (
            <Chip
              key={a.CoveringAttorney_guid}
              size="small"
              color="info"
              variant="outlined"
              label={a.CoveringAttorney_Name}
              disabled={saving}
              onDelete={() =>
                change(() => CoverageApi.removeCourt(a.CoveringAttorney_guid, courtGuid))
              }
            />
          ))}
        </Box>
      )}

      <Box display="flex" gap={1} alignItems="center" mb={2}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 260, flex: 1 }}
          options={addable}
          value={choice}
          onChange={(_, value) => setChoice(value)}
          getOptionLabel={(a) => a.CoveringAttorney_Name}
          isOptionEqualToValue={(a, b) => a.CoveringAttorney_guid === b.CoveringAttorney_guid}
          noOptionsText="Nobody else on the list"
          renderInput={(params) => <TextField {...params} label="Add an attorney who covers here" />}
          disabled={saving}
        />
        <Button size="small" variant="outlined" disabled={!choice || saving} onClick={add}>
          Add
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Someone not in the list? Add them on{' '}
        <Link component={RouterLink} to="/covering-attorneys">
          Covering attorneys
        </Link>{' '}
        first.
      </Typography>

      <Typography variant="subtitle2" gutterBottom>
        Appearances here
      </Typography>
      {appeared.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nobody has a recorded appearance here yet.
        </Typography>
      ) : (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {appeared.map((a) => (
            <Chip
              key={a.guid}
              size="small"
              variant="outlined"
              component={RouterLink}
              to="/covering-attorneys"
              clickable
              label={`${a.name} · ${a.appearances} appearance${a.appearances === 1 ? '' : 's'}, last ${a.lastOn}`}
            />
          ))}
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CourtCoveringAttorneys;
